import type { ChauffeurPage } from "../slug-chauffeur";

// Placeholder visuel en attendant les vraies photos A8 L Security / S680 Guard
// (à déposer dans app/assets/images/reservation/, cf. CLASS_SECURITY).
import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const chauffeurSecurite: ChauffeurPage = {
  slug: "chauffeur-securite",
  title: "Chauffeur de sécurité & véhicule blindé",
  subtitle:
    "Transport sécurisé avec chauffeur formé et véhicule blindé sur la Côte d'Azur.",
  keywords: [
    "chauffeur sécurité",
    "véhicule blindé avec chauffeur",
    "voiture blindée Cannes",
    "transport sécurisé Côte d'Azur",
  ],
  hero: {
    title: "Chauffeur de sécurité & véhicule blindé sur la Côte d'Azur",
    subtitle:
      "Audi A8 L Security, Mercedes S680 Guard — discrétion totale, chauffeurs expérimentés, mobilité sereine pour clientèle exigeante.",
    keywords: ["véhicule blindé", "chauffeur sécurité", "VIP discrétion"],
    imageSrc: heroImage.src,
    imageAlt: "Véhicule blindé avec chauffeur de sécurité sur la Côte d'Azur",
    badges: [
      "Véhicules blindés",
      "Chauffeurs expérimentés",
      "Discrétion totale",
    ],
  },
  benefits: {
    heading: "Pourquoi un transport blindé avec KS Rent Services",
    items: [
      {
        title: "Véhicules blindés",
        description:
          "Berlines blindées Audi A8 L Security et Mercedes-Benz S680 Guard, alliant protection et confort haut de gamme.",
      },
      {
        title: "Chauffeurs expérimentés",
        description:
          "Conduite défensive, anticipation et sang-froid : nos chauffeurs sont rodés aux déplacements sensibles.",
      },
      {
        title: "Discrétion absolue",
        description:
          "Tenue neutre, véhicules sobres, confidentialité stricte sur vos itinéraires et vos rendez-vous.",
      },
      {
        title: "Coordination sur mesure",
        description:
          "Reconnaissance d'itinéraire, horaires confidentiels, points de prise en charge privés et flexibilité totale.",
      },
    ],
  },
  services: {
    heading: "Prestations de transport sécurisé",
    services: [
      {
        title: "Transferts sécurisés",
        text: "Aéroport de Nice, héliport de Monaco, hôtels et résidences privées, en toute sérénité.",
      },
      {
        title: "Mobilité de personnalités",
        text: "Déplacements business, dirigeants, familles fortunées et personnalités publiques.",
      },
      {
        title: "Événements sensibles",
        text: "Festivals, sommets, soirées privées : accès maîtrisés et logistique discrète.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur et véhicule blindé à l'heure ou à la journée, avec le même interlocuteur.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Une mobilité protégée, sans compromis sur le confort",
    subheading:
      "Le niveau de protection au service d'une expérience premium, pas l'inverse.",
    paragraphs: [
      "Nos véhicules blindés conservent l'habitacle, le silence et le raffinement d'une berline de prestige, tout en offrant un niveau de protection adapté aux trajets sensibles.",
      "Chaque mission est préparée en amont : itinéraires, créneaux horaires et points de prise en charge sont définis avec vous et tenus confidentiels.",
      "Vous bénéficiez d'un interlocuteur unique, d'un suivi en temps réel et d'une capacité d'ajustement immédiate selon le contexte.",
    ],
    bullets: [
      "Itinéraires reconnus à l'avance",
      "Confidentialité contractuelle",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment se déroule une mission sécurisée",
    steps: [
      "Échange confidentiel sur votre besoin, vos contraintes et vos horaires.",
      "Préparation de l'itinéraire et des points de prise en charge.",
      "Affectation du véhicule blindé et du chauffeur adapté.",
      "Exécution discrète avec suivi en temps réel et ajustements terrain.",
    ],
  },
  faq: [
    {
      q: "Quels véhicules blindés proposez-vous ?",
      a: "Des berlines blindées de prestige : Audi A8 L Security et Mercedes-Benz S680 Guard, qui conservent le confort d'une limousine tout en offrant une protection renforcée.",
    },
    {
      q: "Vos chauffeurs sont-ils formés aux trajets sensibles ?",
      a: "Oui. Nos chauffeurs pratiquent une conduite anticipative et discrète, habitués aux déplacements de clientèle exigeante et aux contextes sensibles.",
    },
    {
      q: "La confidentialité est-elle garantie ?",
      a: "Absolument : tenue neutre, véhicules sobres et confidentialité stricte sur vos itinéraires, horaires et rendez-vous.",
    },
    {
      q: "Couvrez-vous les grands événements de la Côte d'Azur ?",
      a: "Oui, nous intervenons sur les festivals, sommets et soirées privées, avec une logistique d'accès maîtrisée.",
    },
    {
      q: "Comment obtenir un tarif ?",
      a: "Le transport sécurisé est établi sur devis personnalisé selon l'itinéraire, la durée et le niveau de service. Contactez-nous pour une proposition rapide et confidentielle.",
    },
  ],
  relatedLinks: [
    { label: "Transferts avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Chauffeur pour entreprises", href: "/chauffeur/entreprise" },
    {
      label: "Nos transferts avec chauffeur",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Un déplacement sensible à organiser ?",
    text: "Parlons-en en toute confidentialité — devis rapide pour votre transport blindé.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis confidentiel",
  },
  seo: {
    title: "Chauffeur de sécurité & véhicule blindé — Côte d'Azur",
    description:
      "Transport sécurisé avec chauffeur et véhicule blindé (Audi A8 L Security, Mercedes S680 Guard) à Cannes, Monaco, Nice. Discrétion totale, devis confidentiel.",
    canonical: "/chauffeur/chauffeur-securite",
  },
};

export default chauffeurSecurite;
